import React, { useCallback, useEffect, useState } from 'react';
import { distinguishingLabel } from './senseLabel';
import useYasna from './useYasna';
import './SensePicker.css';

/**
 * SensePicker — annotator search against yasna. Lists every sense of the
 * spelling (yasna's priority order) so the annotator picks the one this
 * subtitle actually uses: "suis" in "je suis fatigué" is être, not suivre.
 * Searches the initial term straight away, so an already-known word is one
 * click from linked.
 *
 * Props:
 *   initialTerm — usually the selected surface text
 *   language — 'fr' | 'de'
 *   selectedId — currently chosen word_sense_id (highlighted)
 *   onSelect(sense) — a yasna entry document
 *   renderExtra(sense) — optional extra content per row (the annotate page
 *                        shows each sense's annotation count)
 */
function SensePicker({ initialTerm = '', language, selectedId, onSelect, renderExtra }) {
  const { yasna, error: loadError } = useYasna();
  const [term, setTerm] = useState(initialTerm);
  const [searched, setSearched] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');

  const run = useCallback(async (text) => {
    const q = text.trim();
    if (!yasna || !q) return;
    setSearching(true);
    setError('');
    try {
      setResults(await yasna.search(q, language));
    } catch (e) {
      setResults([]);
      setError("Couldn't reach the dictionary.");
    }
    setSearched(q);
    setSearching(false);
  }, [yasna, language]);

  useEffect(() => {
    run(initialTerm);
    // Only re-run automatically when the module arrives or the language changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yasna, language]);

  if (loadError) return <p className="yasna-status yasna-status--error">{loadError}</p>;
  if (!yasna) return <p className="yasna-status">Loading dictionary…</p>;

  const teacherUrl = searched ? yasna.teacherUrl(searched, language) : null;

  return (
    <div className="sense-picker">
      <form
        className="sense-picker-search"
        onSubmit={(e) => { e.preventDefault(); run(term); }}
      >
        <input
          className="sense-picker-input"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search the dictionary"
          aria-label="Search the dictionary"
          autoCapitalize="off"
          spellCheck="false"
        />
        <button className="sense-picker-go" type="submit" disabled={searching || !term.trim()}>
          {searching ? '…' : 'Search'}
        </button>
      </form>

      {error && <p className="yasna-status yasna-status--error">{error}</p>}

      {!searching && !error && searched && results.length === 0 && (
        <div className="sense-picker-missing">
          <p>"{searched}" isn't in the dictionary yet.</p>
          <a href={teacherUrl} target="_blank" rel="noreferrer">Add it in Yasna ↗</a>
          <p className="sense-picker-hint">Once it's saved there, search again here.</p>
        </div>
      )}

      {results.length > 0 && (
        <ul className="sense-picker-results" aria-label="Dictionary senses">
          {results.map((s) => (
            <li key={s.word_sense_id}>
              <button
                type="button"
                className={`sense-picker-row ${s.word_sense_id === selectedId ? 'sense-picker-row--selected' : ''}`}
                aria-pressed={s.word_sense_id === selectedId}
                onClick={() => onSelect(s)}
              >
                <span className="sense-picker-word">{s.word}</span>
                {distinguishingLabel(s, results) && <span className="sense-picker-label">{distinguishingLabel(s, results)}</span>}
                {s.short_gloss && <span className="sense-picker-gloss">{s.short_gloss}</span>}
                {renderExtra && renderExtra(s)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SensePicker;
